import React, { FC, useCallback, useState } from 'react';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import { object, string } from 'yup';
import { titleize } from 'inflection';
import { yupResolver } from '@hookform/resolvers/yup';

import AttachmentDropzoneField from 'buyside/components/presentation/WhitePaperForm/WriteupAndAttachments/Attachments/AttachmentForm/attachmentDropzoneField';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import Modal from 'buyside/components/presentation/Modal/modal';
import SelectField from 'buyside/components/presentation/Fields/selectField';

import { labelize } from 'buyside/util/arrayHelper';
import { WhitePaperFormInput } from 'buyside/util/researchSubmission';

const { whitePaperAttachmentTypes } = require('buyside/util/constantsFromRails.json');

import styles from './attachmentFormStyles.scss';

const AttachmentForm: FC<{
  attachment?: WhitePaperFormInput['attachments'][number];
  onSubmitCallback: (values: WhitePaperFormInput['attachments'][number]) => void;
  researchType: string;
}> = ({ attachment, onSubmitCallback, researchType }) => {
  const { isSubmitting } = useFormState();

  const isThesis = researchType === 'thesis';
  const titleizedResearchType = titleize(researchType);

  const [isOpen, setIsOpen] = useState(false);

  const methods = useForm<WhitePaperFormInput['attachments'][number]>({
    values: attachment || {
      id: null,
      attachmentType: isThesis ? 'thesis' : '',
      file: null,
      filename: '',
      researchType,
      url: ''
    },
    resolver: yupResolver(
      object({
        attachmentType: string().required('Please select an attachment type'),
        filename: string().required('Please select a file'),
        researchType: string().required()
      })
    )
  });

  const { handleSubmit, reset } = methods;

  const handleOnClose = useCallback(() => {
    reset();
    setIsOpen(false);
  }, [reset]);

  const renderOpenModalButton = useCallback(
    () => (
      <Button
        buttonType={BUTTON_TYPES.BUTTON}
        className={styles.attachmentFormButton}
        disabled={isSubmitting}
        faIcon={attachment ? null : faPlus}
        onClick={() => setIsOpen(true)}
        size={BUTTON_SIZES.SMALL}
        text={`${attachment ? 'Edit' : `Attach ${titleizedResearchType}`}`}
        type={BUTTON_TYPES.LINK}
      />
    ),
    [attachment, isSubmitting, titleizedResearchType]
  );

  const handleOnSubmit = handleSubmit(values => {
    onSubmitCallback(values);
    handleOnClose();
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      renderOpenModalButton={renderOpenModalButton}
      title={`${attachment ? 'Edit' : 'Attach'} ${titleizedResearchType}`}
    >
      <FormProvider {...methods}>
        <div className={styles.fields}>
          {isThesis ? null : (
            <SelectField
              label="Attachment Type"
              name="attachmentType"
              options={labelize(whitePaperAttachmentTypes[researchType], true)}
            />
          )}
          <AttachmentDropzoneField isThesis={isThesis} />
        </div>
        <div className={styles.submissionButtons}>
          <Button
            buttonType={BUTTON_TYPES.BUTTON}
            disabled={isSubmitting}
            onClick={handleOnSubmit}
            size={BUTTON_SIZES.SMALL}
            text={`${attachment ? 'Update' : 'Attach'} ${titleizedResearchType}`}
            type={BUTTON_TYPES.PRIMARY}
          />
          <Button
            buttonType={BUTTON_TYPES.BUTTON}
            disabled={isSubmitting}
            onClick={handleOnClose}
            size={BUTTON_SIZES.SMALL}
            text="Cancel"
          />
        </div>
      </FormProvider>
    </Modal>
  );
};

export default AttachmentForm;
